import React, { useState } from 'react';
import { CONTACT_INQUIRY_URL } from '../config/api';

const ContactUs = () => {
  const [form, setForm] = useState({
    name: '',
    email: '',
    company: '',
    message: '',
  });
  const [submitState, setSubmitState] = useState('idle');
  const [submitMessage, setSubmitMessage] = useState('');

  const handleChange = e => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async e => {
    e.preventDefault();
    if (submitState === 'submitting') return;

    setSubmitState('submitting');
    setSubmitMessage('');

    try {
      const response = await fetch(CONTACT_INQUIRY_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });

      if (!response.ok) throw new Error('Contact inquiry failed');

      setForm({ name: '', email: '', company: '', message: '' });
      setSubmitState('success');
      setSubmitMessage('Message sent. We will get back to you within one business day.');
    } catch (_) {
      setSubmitState('error');
      setSubmitMessage('We could not send your message. Please try again or email us directly.');
    }
  };

  return (
    <div className="contact-root">
      <style>{`
        :root {
          --green: #7ED321;
          --text-dark: #0f172a;
          --text-muted: #475569;
          --border: #e5e7eb;
          --bg-soft: #f8fafc;
        }

        body {
          margin: 0;
          font-family: 'Inter', sans-serif;
          background: #ffffff;
          color: var(--text-dark);
        }

        .contact-root {
          min-height: 100vh;
        }

        /* ---------- INTRO ---------- */

        .contact-hero {
          max-width: 1100px;
          margin: auto;
          padding: 160px 6vw 100px;
        }

        .contact-hero span {
          font-size: 13px;
          font-weight: 700;
          letter-spacing: 0.12em;
          text-transform: uppercase;
          color: var(--green);
        }

        .contact-hero h1 {
          font-size: clamp(40px, 5vw, 56px);
          font-weight: 800;
          margin: 24px 0 20px;
          letter-spacing: -0.02em;
        }

        .contact-hero p {
          font-size: 18px;
          line-height: 1.7;
          color: var(--text-muted);
          max-width: 720px;
        }

        /* ---------- GRID ---------- */

        .contact-grid {
          max-width: 1100px;
          margin: auto;
          padding: 0 6vw 140px;
          display: grid;
          grid-template-columns: 1.2fr 0.8fr;
          gap: 64px;
        }

        /* ---------- FORM ---------- */

        .contact-form {
          background: #fff;
          border: 1px solid var(--border);
          border-radius: 28px;
          padding: 48px;
        }

        .contact-form h3 {
          font-size: 24px;
          font-weight: 800;
          margin-bottom: 32px;
        }

        .field {
          display: flex;
          flex-direction: column;
          gap: 8px;
          margin-bottom: 24px;
        }

        .field label {
          font-size: 14px;
          font-weight: 600;
          color: var(--text-dark);
        }

        .field input,
        .field textarea {
          padding: 14px 16px;
          font-size: 15px;
          border-radius: 10px;
          border: 1px solid var(--border);
          outline: none;
          transition: border 0.2s ease, box-shadow 0.2s ease;
        }

        .field input:focus,
        .field textarea:focus {
          border-color: var(--green);
          box-shadow: 0 0 0 3px rgba(126, 211, 33, 0.15);
        }

        .field textarea {
          resize: vertical;
          min-height: 120px;
        }

        .submit-btn {
          margin-top: 12px;
          padding: 14px 28px;
          background: var(--green);
          color: #00351f;
          border-radius: 12px;
          font-size: 15px;
          font-weight: 600;
          border: none;
          cursor: pointer;
          transition: transform 0.2s ease, box-shadow 0.2s ease;
        }

        .submit-btn:hover {
          transform: translateY(-2px);
          box-shadow: 0 12px 30px rgba(126, 211, 33, 0.35);
        }

        .submit-btn:disabled {
          opacity: 0.65;
          cursor: wait;
          transform: none;
          box-shadow: none;
        }

        .form-feedback {
          margin: 16px 0 0;
          font-size: 14px;
          line-height: 1.5;
          color: var(--text-muted);
        }

        /* ---------- INFO ---------- */

        .contact-info {
          background: var(--bg-soft);
          border-radius: 28px;
          padding: 48px;
          height: fit-content;
        }

        .info-block {
          margin-bottom: 36px;
        }

        .info-block h4 {
          font-size: 14px;
          font-weight: 700;
          letter-spacing: 0.12em;
          text-transform: uppercase;
          color: var(--green);
          margin-bottom: 10px;
        }

        .info-block p {
          font-size: 16px;
          line-height: 1.6;
          color: var(--text-dark);
        }

        .info-muted {
          font-size: 14px;
          color: var(--text-muted);
          margin-top: 4px;
        }

        /* ---------- FOOT NOTE ---------- */

        .contact-note {
          max-width: 1100px;
          margin: auto;
          padding: 0 6vw 120px;
          text-align: center;
          font-size: 15px;
          color: var(--text-muted);
        }

        @media (max-width: 900px) {
          .contact-grid {
            grid-template-columns: 1fr;
            gap: 40px;
          }

          .contact-form,
          .contact-info {
            padding: 32px;
          }
        }

        @media (max-width: 768px) {
          .contact-hero {
            padding: 140px 5vw 80px;
          }

          .contact-hero h1 {
            font-size: clamp(32px, 4.5vw, 40px);
          }

          .contact-hero p {
            font-size: 16px;
          }

          .contact-grid {
            padding: 0 5vw 100px;
            gap: 30px;
          }

          .contact-form,
          .contact-info {
            padding: 28px;
            border-radius: 20px;
          }

          .contact-note {
            padding: 0 5vw 80px;
            font-size: 14px;
          }
        }

        @media (max-width: 480px) {
          .contact-hero {
            padding: 120px 4vw 60px;
          }

          .contact-hero h1 {
            font-size: 28px;
            margin: 20px 0 16px;
          }

          .contact-hero p {
            font-size: 15px;
          }

          .contact-grid {
            padding: 0 4vw 80px;
            gap: 24px;
          }

          .contact-form,
          .contact-info {
            padding: 24px;
            border-radius: 16px;
          }

          .contact-form h3 {
            font-size: 20px;
            margin-bottom: 24px;
          }

          .field {
            margin-bottom: 20px;
          }

          .field label {
            font-size: 13px;
          }

          .field input,
          .field textarea {
            padding: 12px 14px;
            font-size: 14px;
          }

          .submit-btn {
            padding: 12px 24px;
            font-size: 14px;
            width: 100%;
          }

          .info-block {
            margin-bottom: 28px;
          }

          .info-block h4 {
            font-size: 13px;
          }

          .info-block p {
            font-size: 15px;
          }

          .info-muted {
            font-size: 13px;
          }

          .contact-note {
            padding: 0 4vw 60px;
          }
        }
      `}</style>

      {/* INTRO */}
      <section className="contact-hero">
        <span>Contact Us</span>
        <h1>Let’s talk about your loan needs</h1>
        <p>
          Whether you’re exploring funding options or have a specific question,
          our team is here to help.
        </p>
      </section>

      {/* GRID */}
      <section className="contact-grid">
        {/* FORM */}
          <form className="contact-form" onSubmit={handleSubmit}>
            <h3>Send us a message</h3>

            <div className="field">
            <label htmlFor="contact-name">Full name</label>
            <input
              id="contact-name"
              name="name"
              value={form.name}
              onChange={handleChange}
              placeholder="Your name"
              autoComplete="name"
              required
            />
          </div>

          <div className="field">
            <label htmlFor="contact-email">Email address</label>
            <input
              id="contact-email"
              name="email"
              type="email"
              value={form.email}
              onChange={handleChange}
              placeholder="you@company.com"
              autoComplete="email"
              required
            />
          </div>

          <div className="field">
            <label htmlFor="contact-company">Company</label>
            <input
              id="contact-company"
              name="company"
              value={form.company}
              onChange={handleChange}
              placeholder="Business name"
              autoComplete="organization"
            />
          </div>

          <div className="field">
            <label htmlFor="contact-message">Message</label>
            <textarea
              id="contact-message"
              name="message"
              value={form.message}
              onChange={handleChange}
              placeholder="Tell us briefly what you'd like to discuss"
              required
            />
          </div>

          <button className="submit-btn" type="submit" disabled={submitState === 'submitting'}>
            {submitState === 'submitting' ? 'Sending…' : 'Submit'}
          </button>
          {submitState === 'success' && <p className="form-feedback" role="status">{submitMessage}</p>}
          {submitState === 'error' && <p className="form-feedback" role="alert">{submitMessage}</p>}
        </form>

        {/* INFO */}
       <div className="contact-info">
  <div className="info-block">
    <h4>Email</h4>
    <p>support@xtremeindia.co.in</p>
    <div className="info-muted">We usually respond within one business day</div>
  </div>

  <div className="info-block">
    <h4>Phone</h4>
    <p>+91 8130901417</p>
    <div className="info-muted">Monday – Saturday, 9am – 6pm</div>
  </div>

  <div className="info-block">
    <h4>Registered Address</h4>
    <p>
      836 R, Near Pioneer Convent School,<br />
      Mahalaxmi Nagar, Indore (MP) 452010
    </p>
  </div>

  <div className="info-block">
    <h4>Head Office</h4>
    <p>
      1st Floor, Subhadra House, 11,<br />
      Joy Builder's Colony, Saket,<br />
      Indore (MP) 452018
    </p>
  </div>
</div>
          
      </section>

      {/* FOOT NOTE */}
      <div className="contact-note">
        Your information is used only to respond to your inquiry.
      </div>
    </div>
  );
};

export default ContactUs;
